/** Configuration navigation panel — source unique pour le menu latéral. */
export type PanelNavItemConfig = {
  to: string;
  label: string;
  section: string;
  defaultAccess: (roleId: number) => boolean;
  activePrefix?: string;
};
export type PanelNavGroupConfig = {
  id: string;
  label: string;
  items: PanelNavItemConfig[];
};
export type PanelSalonConfig = {
  id: string;
  label: string;
  subtitle: string;
  to: string;
  section: string;
  accent: 'amber' | 'sky' | 'yellow' | 'violet' | 'cyan';
  defaultAccess: (roleId: number) => boolean;
  loadingFallback: (roleId: number) => boolean;
};
export const PANEL_NAV_GROUPS: PanelNavGroupConfig[] = [
  {
    id: 'overview',
    label: "Vue d'ensemble",
    items: [
      {
        to: '/panel/dashboard',
        label: 'Dashboard',
        section: 'stats',
        defaultAccess: (r) => r >= 1 && r <= 4,
        activePrefix: '/panel/dashboard',
      },
    ],
  },
  {
    id: 'joueurs',
    label: 'Joueurs',
    items: [
      {
        to: '/panel/joueurs',
        label: 'Joueurs',
        section: 'salon_joueurs',
        defaultAccess: (r) => r >= 1 && r <= 4,
        activePrefix: '/panel/joueurs',
      },
      {
        to: '/panel/moderation',
        label: 'Modération',
        section: 'moderation',
        defaultAccess: (r) => r >= 1 && r <= 3,
        activePrefix: '/panel/moderation',
      },
    ],
  },
  {
    id: 'scenarios',
    label: 'Scénarios & CTA',
    items: [
      {
        to: '/panel/scenarios',
        label: 'Scénarios & CTA',
        section: 'interventions',
        defaultAccess: (r) => r >= 1 && r <= 4,
        activePrefix: '/panel/scenarios',
      },
    ],
  },
  {
    id: 'logs',
    label: 'Sécurité',
    items: [
      {
        to: '/panel/logs',
        label: 'Logs & sécurité',
        section: 'salon_security',
        defaultAccess: (r) => r >= 1 && r <= 3,
        activePrefix: '/panel/logs',
      },
    ],
  },
];
export const PANEL_SALONS: PanelSalonConfig[] = [
  {
    id: 'cm',
    label: 'CM',
    subtitle: 'Tout le panel + système',
    to: '/panel/cm',
    section: 'salon_cm',
    accent: 'amber',
    defaultAccess: (r) => r === 1,
    loadingFallback: (r) => r === 1,
  },
  {
    id: 'admin',
    label: 'Administrateur',
    subtitle: 'Joueurs & statut premium (outil staff)',
    to: '/panel/admin',
    section: 'salon_admin',
    accent: 'sky',
    defaultAccess: (r) => r === 1 || r === 2,
    loadingFallback: (r) => r === 1 || r === 2,
  },
  {
    id: 'modo',
    label: 'Modérateur',
    subtitle: 'Surveillance & modération',
    to: '/panel/modo',
    section: 'salon_modo',
    accent: 'yellow',
    defaultAccess: (r) => r === 1 || r === 3,
    loadingFallback: (r) => r === 1 || r === 3,
  },
  {
    id: 'gr-scenarios',
    label: 'GR — Scénarios',
    subtitle: 'CTA, scénarios & missions (Groupe de réflexion)',
    to: '/panel/scenariste',
    section: 'salon_scenariste',
    accent: 'violet',
    defaultAccess: (r) => r === 1 || r === 4,
    loadingFallback: (r) => r === 1 || r === 4,
  },
  {
    id: 'referents',
    label: 'Référents',
    subtitle: 'Fonctions référent assignées',
    to: '/panel/ref',
    section: 'salon_ref',
    accent: 'cyan',
    defaultAccess: (r) => r === 4,
    loadingFallback: (r) => r === 4,
  },
];
export const ROLE_LABELS: Record<number, string> = {
  1: 'Community Manager',
  2: 'Administrateur',
  3: 'Modérateur',
  4: 'Groupe de réflexion',
  5: 'Créateur de contenu',
  6: 'Joueur',
};
export function resolvePageTitle(pathname: string): string {
  if (pathname.startsWith('/panel/cm/maintenance')) return 'Maintenance';
  if (pathname.startsWith('/panel/cm/referent')) return 'Référent CM';
  if (pathname.startsWith('/panel/cm')) return 'Espace CM';
  if (pathname.startsWith('/panel/admin')) return 'Espace administrateur';
  if (pathname.startsWith('/panel/modo')) return 'Espace modérateur';
  if (pathname.startsWith('/panel/scenariste')) return 'Espace GR — scénarios';
  if (pathname.startsWith('/panel/ref')) return 'Espace référent';
  if (pathname.startsWith('/panel/dashboard') || pathname === '/panel') return 'Dashboard';
  if (pathname.startsWith('/panel/joueurs/liste')) return 'Joueurs — Liste';
  if (pathname.startsWith('/panel/joueurs/fiches')) return 'Joueurs — Fiches';
  if (pathname.startsWith('/panel/joueurs/premium')) return 'Joueurs — Premium';
  if (pathname.startsWith('/panel/joueurs')) return 'Joueurs';
  if (pathname.startsWith('/panel/scenarios/cta')) return 'Scénarios — CTA';
  if (pathname.startsWith('/panel/scenarios/gerer')) return 'Scénarios — Gérer';
  if (pathname.startsWith('/panel/scenarios/edit')) return 'Scénarios — Modifier';
  if (pathname.startsWith('/panel/scenarios/creer')) return 'Scénarios — Créer';
  if (pathname.startsWith('/panel/scenarios/categories')) return 'Scénarios — Catégories';
  if (pathname.startsWith('/panel/scenarios/radio')) return 'Scénarios — Radio ANTARES';
  if (pathname.startsWith('/panel/scenarios')) return 'Scénarios & CTA';
  if (pathname.startsWith('/panel/logs/synthese')) return 'Logs — Synthèse';
  if (pathname.startsWith('/panel/logs/complet')) return 'Logs — Complet';
  if (pathname.startsWith('/panel/logs')) return 'Logs & sécurité';
  if (pathname.startsWith('/panel/support/team')) return 'Tickets équipe';
  if (pathname.startsWith('/panel/users/roles')) return 'Rôles utilisateurs';
  if (pathname.startsWith('/panel/roles/default-access')) return 'Accès par défaut (rôles)';
  if (pathname.startsWith('/panel/roles/individual')) return 'Rôles individuels';
  if (pathname.startsWith('/panel/moderation/manage')) return 'Gestion joueur';
  if (pathname.startsWith('/panel/moderation')) return 'Modération';
  if (pathname.startsWith('/panel/system/backup')) return 'Sauvegardes';
  if (pathname.startsWith('/panel/config')) return 'Configuration métier';
  return 'Panel';
}
